import { useLayoutEffect, useRef } from 'react';

/**
 * Leading-edge debounce: fires on the first call, ignores subsequent calls within `delay` ms.
 * Correct for action buttons — no perceptible latency, no double-fire.
 */
export function useDebouncedCallback<T extends (...args: Parameters<T>) => unknown>(
  fn: T,
  delay = 300,
): (...args: Parameters<T>) => void {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fnRef = useRef(fn);

  useLayoutEffect(() => {
    fnRef.current = fn;
  });

  return (...args: Parameters<T>) => {
    if (timerRef.current !== null) return;
    fnRef.current(...args);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
    }, delay);
  };
}
